import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from '../app/store';
import type {VehicleDetailsCardDTO} from "../api/generated";
import {getWidgetControllerApi} from "./chatbotSlice";

interface VehicleDetailsState {
    fullVehicleDetails: Record<string, VehicleDetailsCardDTO>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
    openedVehicleCardVin: string | null;
}

interface FetchVehicleDetailsPayload {
    vin: string;
    publicBotId: string;
}


const OPENED_VEHICLE_VIN_KEY = 'openedVehicleCardVin';
const VEHICLE_CACHE_KEY_PREFIX = 'vehicleCardData_';
const CACHE_EXPIRY_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds

const getFromLocalStorage = <T>(key: string): T | null => {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
    } catch (e) {
        console.error('Error reading from localStorage:', e);
        return null;
    }
};

const saveToLocalStorage = <T>(key: string, data: T): void => {
    try {
        const item = {
            data,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem(key, JSON.stringify(item));
    } catch (e) {
        console.error('Error saving to localStorage:', e);
    }
};

const isStoredDataValid = (key: string): boolean => {
    const cachedData = getFromLocalStorage<{ timestamp: string }>(key);
    if (!cachedData?.timestamp) {
        return false;
    }
    return new Date(cachedData.timestamp) > new Date(Date.now() - CACHE_EXPIRY_TIME);
};

const getFullVehicleDetails = () => {
    const openedVehicleCardVin = getFromLocalStorage<string>(OPENED_VEHICLE_VIN_KEY);
    const fullVehicleDetails = openedVehicleCardVin ? getFromLocalStorage<{
        data: VehicleDetailsCardDTO
    }>(`${VEHICLE_CACHE_KEY_PREFIX}${openedVehicleCardVin}`) : null;
    return fullVehicleDetails?.data ? {[openedVehicleCardVin as string]: fullVehicleDetails.data} : {};
}

const initialState: VehicleDetailsState = {
    fullVehicleDetails: getFullVehicleDetails(),
    loading: {},
    error: {},
    openedVehicleCardVin: getFromLocalStorage<string>(OPENED_VEHICLE_VIN_KEY)
}

export const fetchFullVehicleDetails = createAsyncThunk<VehicleDetailsCardDTO, FetchVehicleDetailsPayload, {
    state: RootState,
    rejectValue: string
}>('vehicleDetails/fetchFullVehicleDetails', async (payload, {getState}) => {
        const {vin, publicBotId} = payload;
        if (!vin || !publicBotId) {
            throw new Error("Missed vin or publicBotId")
        }

        const cacheKey = `${VEHICLE_CACHE_KEY_PREFIX}${vin}`;
        const cachedData = getFromLocalStorage<{ data: VehicleDetailsCardDTO }>(cacheKey);
        if (cachedData?.data && isStoredDataValid(cacheKey)) {
            return cachedData.data;
        }

        return getWidgetControllerApi(getState).getVehicleDetailsCardContent({publicBotId, vin})
    }
);

export const vehicleDetailsSlice = createSlice({
    name: 'vehicleDetails',
    initialState,
    reducers: {
        clearVehicleDetails: (state, action: PayloadAction<string>) => {
            const vin = action.payload;
            delete state.fullVehicleDetails[vin];
            delete state.loading[vin];
            delete state.error[vin];

            try {
                localStorage.removeItem(`${VEHICLE_CACHE_KEY_PREFIX}${vin}`);
            } catch (e) {
                console.error('Error removing item from localStorage:', e);
            }
        },
        setOpenedVehicleCardVin: (state, action: PayloadAction<string | null>) => {
            state.openedVehicleCardVin = action.payload;

            try {
                if (action.payload) {
                    localStorage.setItem(OPENED_VEHICLE_VIN_KEY, JSON.stringify(action.payload));
                } else {
                    localStorage.removeItem(OPENED_VEHICLE_VIN_KEY);
                }
            } catch (e) {
                console.error('Error updating localStorage:', e);
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFullVehicleDetails.pending, (state, action) => {
                const payload = action.meta.arg;
                state.loading[payload.vin] = true
                state.error[payload.vin] = null;
            })
            .addCase(fetchFullVehicleDetails.fulfilled, (state, action) => {
                const payload = action.meta.arg;
                state.fullVehicleDetails[payload.vin] = action.payload;
                saveToLocalStorage(`${VEHICLE_CACHE_KEY_PREFIX}${payload.vin}`, action.payload);
                state.loading[payload.vin] = false;
            })
            .addCase(fetchFullVehicleDetails.rejected, (state, action) => {
                const payload = action.meta.arg;
                state.loading[payload.vin] = false;
                state.error[payload.vin] = action.payload as string || 'Failed to fetch vehicle details';
            });
    },
});

export const {
    clearVehicleDetails,
    setOpenedVehicleCardVin
} = vehicleDetailsSlice.actions;

export const selectFullVehicleDetails = (state: RootState, vin: string): VehicleDetailsCardDTO | undefined => state.vehicleDetails.fullVehicleDetails[vin];
export const selectVehicleDetailsLoading = (state: RootState, vin: string): boolean => state.vehicleDetails.loading[vin];
export const selectVehicleDetailsError = (state: RootState, vin: string): string | null => state.vehicleDetails.error[vin] || null;
export const selectOpenedVehicleCardVin = (state: RootState): string | null => state.vehicleDetails.openedVehicleCardVin;

export default vehicleDetailsSlice.reducer;
